import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { dateToISO, Shift, updateShift } from "@enos-monorepo/lib";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import { useEffect,useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface EditActiveShiftModalProps extends ModalProps {
  shift: Shift;
  companyLocationId: string;
  companyId: string;
}

export function EditActiveShiftModal({
  shift,
  companyLocationId,
  companyId,
  ...props
}: EditActiveShiftModalProps) {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [actualTime, setActualTime] = useState("");

  const onSubmit = () => {
    const startDate: Date | null = shift?.plannedStartDate
      ? new Date(shift.plannedStartDate)
      : null;
    const endDate: Date | null = shift?.plannedEndDate
      ? new Date(shift.plannedEndDate)
      : null;
    const actualDate: Date = new Date(shift?.workStartDate ?? "");

    if (startTime && startDate) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      startDate.setHours(startHours, startMinutes, 0, 0);
    }
    if (endTime && endDate) {
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      endDate.setHours(endHours, endMinutes, 0, 0);
    }
    if (actualTime) {
      const [actualHours, actualMinutes] = actualTime.split(":").map(Number);
      actualDate.setHours(actualHours, actualMinutes, 0);
    }
    if (startDate && endDate) {
      const payload = {
        id: shift?.id ?? "",
        companyId: companyId,
        companyLocationId: companyLocationId,
        plannedStartDate: startDate ? dateToISO(startDate) : "",
        plannedEndDate: endDate ? dateToISO(endDate) : "",
        workStartDate: dateToISO(actualDate),
        userId: shift?.userId ?? "",
      };
      mutation.mutate(payload);
      return;
    }
    const payload = {
      id: shift?.id ?? "",
      companyId: companyId,
      companyLocationId: companyLocationId,
      workStartDate: dateToISO(actualDate),
      userId: shift?.userId ?? "",
    };
    mutation.mutate(payload);
  };

  useEffect(() => {
    // if there is no planned time for an active shift and user wants to add it
    if (startTime && endTime) {
      const currentDate = new Date();
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      currentDate.setHours(startHours, startMinutes, 0, 0);
      const endDate = new Date(currentDate);
      endDate.setHours(endHours, endMinutes, 0, 0);
      shift.plannedStartDate = dateToISO(currentDate);
      shift.plannedEndDate = dateToISO(endDate);
    }
  }, [startTime, endTime, shift]);

  useEffect(() => {
    setStartTime(shift?.plannedStartDate?.split("T")[1] ?? "");
    setEndTime(shift?.plannedEndDate?.split("T")[1] ?? "");

    //actual time coming with seconds, we need to remove them
    const act = shift?.workStartDate?.split("T")[1].split(":");
    if (act) act.pop();
    setActualTime(act?.join(":") ?? "");
  }, [shift]);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateShift,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shifts"],
      });
      setStartTime("");
      setEndTime("");
      props.onClose?.();
      toast.success(t("shift.The shift has been edited"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to edit the shift"));
    },
  });

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={onSubmit}>
            {t("shift.Plan Shifts")}
          </Button>
        </>
      }
      {...props}
      title={`${t("shift.Edit an Active Shift")}: ${shift?.firstName + " " + shift?.lastName}`}
    >
      <form>
        <Label title={t("shift.Planned Beginning and End")}>
          <div className="flex flex-row justify-between w-full gap-2">
            <div className="flex flex-col w-full gap-y-1">
              <Input
                value={
                  startTime ? startTime : shift?.plannedStartDate?.split("T")[1]
                }
                onChange={(e) => setStartTime(e.target.value)}
                type="time"
              />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <Input
                value={endTime ? endTime : shift?.plannedEndDate?.split("T")[1]}
                onChange={(e) => setEndTime(e.target.value)}
                type="time"
              />
            </div>
          </div>
        </Label>
        <Label title={t("shift.Actual Beginning")}>
          <Input
            value={
              actualTime ? actualTime : shift?.workStartDate?.split("T")[1]
            }
            onChange={(e) => setActualTime(e.target.value)}
            type="time"
          />
        </Label>
      </form>
    </Modal>
  );
}
